

import React, { useState, useEffect } from 'react';
import { Category, Group, ExpenseStatus, Recurrence, Expense } from '../types';
import { X, Check, Camera, DollarSign, Calendar, Tag, Repeat, Users, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';

interface AddExpenseModalProps {
  categories: Category[];
  groups: Group[];
  currentUser: string;
  onClose: () => void;
  onSave: (expenseData: any) => void;
  expenseToEdit?: Expense | null; // Optional prop for editing mode
}

export const AddExpenseModal: React.FC<AddExpenseModalProps> = ({ categories, groups, onClose, onSave, currentUser, expenseToEdit }) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState(categories[0]?.id || '');
  const [groupId, setGroupId] = useState('none');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [recurrence, setRecurrence] = useState<Recurrence>(Recurrence.NONE);
  const [splitEqually, setSplitEqually] = useState(false);
  const [type, setType] = useState<'income' | 'expense'>('expense');
  
  // State for success feedback
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize form if editing
  useEffect(() => {
    if (expenseToEdit) {
      setDescription(expenseToEdit.description);
      // Format amount for input
      setAmount(expenseToEdit.amount.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      setCategoryId(expenseToEdit.categoryId || categories[0]?.id || '');
      setGroupId(expenseToEdit.groupId || 'none');
      setDate(new Date(expenseToEdit.date).toISOString().split('T')[0]);
      setRecurrence(expenseToEdit.recurrence);
      setSplitEqually(expenseToEdit.splitEqually);
      setType(expenseToEdit.type);
    }
  }, [expenseToEdit, categories]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove tudo que não for dígito
    const rawValue = e.target.value.replace(/\D/g, "");
    
    if (!rawValue) {
      setAmount("");
      return;
    }

    // Divide por 100 para considerar os centavos
    const floatValue = parseFloat(rawValue) / 100;

    // Formata para BRL
    const formatted = floatValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    setAmount(formatted);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Converte de volta de "1.234,56" para 1234.56 para salvar no banco
    const numericAmount = parseFloat(amount.replace(/\./g, '').replace(',', '.')) || 0;

    // CORREÇÃO DE FUSO HORÁRIO (TIMEZONE FIX)
    const [y, m, d] = date.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d, 12, 0, 0, 0);

    onSave({
      id: expenseToEdit?.id, // Pass ID if editing
      description,
      amount: numericAmount,
      categoryId: type === 'income' ? null : categoryId,
      groupId: groupId === 'none' ? undefined : groupId,
      date: dateObj.toISOString(),
      status: expenseToEdit ? expenseToEdit.status : ExpenseStatus.PENDING, // Changed default to PENDING
      recurrence,
      paidByUserId: currentUser,
      splitEqually: groupId !== 'none' ? splitEqually : false,
      type
    });

    // Show success feedback
    setShowSuccess(true);

    // Close after delay
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-zinc-900 w-full sm:max-w-md rounded-t-[2.5rem] sm:rounded-[2.5rem] p-8 animate-in slide-in-from-bottom-20 shadow-2xl max-h-[90vh] overflow-y-auto relative overflow-hidden">
        
        {/* Success Overlay */}
        {showSuccess && (
          <div className="absolute inset-0 bg-white dark:bg-zinc-900 z-50 flex flex-col items-center justify-center animate-in fade-in duration-300">
             <div className="w-24 h-24 bg-brand-lime rounded-full flex items-center justify-center mb-6 shadow-glow animate-bounce">
                <Check size={48} className="text-brand-dark" strokeWidth={3} />
             </div>
             <h3 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Salvo!</h3>
             <p className="text-slate-400">Transação {expenseToEdit ? 'atualizada' : 'registrada'} com sucesso.</p>
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-slate-800 dark:text-white">{expenseToEdit ? 'Editar Transação' : 'Nova Transação'}</h3>
          <button onClick={onClose} className="p-3 bg-brand-gray dark:bg-zinc-800 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Income/Expense Toggle */}
          <div className="flex bg-brand-gray dark:bg-zinc-800 p-1 rounded-2xl">
            <button
              type="button"
              onClick={() => { setType('expense'); setGroupId('none'); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all ${type === 'expense' ? 'bg-white dark:bg-zinc-700 shadow-sm text-red-500' : 'text-slate-400'}`}
            >
              <ArrowDownCircle size={18} /> Saída
            </button>
            <button
              type="button"
              onClick={() => { setType('income'); setGroupId('none'); }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all ${type === 'income' ? 'bg-white dark:bg-zinc-700 shadow-sm text-brand-lime' : 'text-slate-400'}`}
            >
              <ArrowUpCircle size={18} /> Entrada
            </button>
          </div>

          {/* Amount Big Input */}
          <div className="bg-brand-gray dark:bg-zinc-800 rounded-3xl p-6 text-center">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Valor Total</label>
            <div className="flex justify-center items-center text-slate-900 dark:text-white">
               <span className="text-3xl font-medium mr-2 text-slate-400">R$</span>
               <input 
                type="tel" 
                inputMode="numeric"
                required
                className="bg-transparent text-5xl font-bold text-slate-900 dark:text-white text-center w-56 focus:outline-none placeholder:text-slate-300 dark:placeholder:text-zinc-600"
                placeholder="0,00"
                value={amount}
                onChange={handleAmountChange}
                autoFocus={!expenseToEdit}
              />
            </div>
          </div>

          <div>
             <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Descrição</label>
             <div className="relative">
               <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><Tag size={20} /></div>
               <input 
                type="text" 
                required
                className="w-full pl-12 pr-4 py-4 bg-brand-gray dark:bg-zinc-800 rounded-2xl text-slate-800 dark:text-white font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-lime transition-all"
                placeholder={type === 'expense' ? "O que você comprou?" : "Origem do recebimento"}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
             </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
             <div className={type === 'income' ? 'col-span-2' : ''}>
               <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Data</label>
               <div className="relative">
                 <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"><Calendar size={20} /></div>
                 <input 
                  type="date" 
                  required
                  className="w-full pl-12 pr-4 py-4 bg-brand-gray dark:bg-zinc-800 rounded-2xl text-slate-800 dark:text-white font-medium focus:outline-none appearance-none"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
               </div>
            </div>
             
             {type === 'expense' && (
               <div>
                 <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Categoria</label>
                 <div className="relative">
                   <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                     <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                   </div>
                   <select 
                    className="w-full pl-4 pr-10 py-4 bg-brand-gray dark:bg-zinc-800 rounded-2xl text-slate-800 dark:text-white font-medium focus:outline-none appearance-none cursor-pointer"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                  >
                    {categories?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
             )}
          </div>

          {/* Recurrence Selector */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Repetição</label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"><Repeat size={20} /></div>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <select
                className="w-full pl-12 pr-10 py-4 bg-brand-gray dark:bg-zinc-800 rounded-2xl text-slate-800 dark:text-white font-medium focus:outline-none appearance-none cursor-pointer"
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value as Recurrence)}
              >
                <option value={Recurrence.NONE}>Sem repetição</option>
                <option value={Recurrence.WEEKLY}>Repetir Semanalmente</option>
                <option value={Recurrence.MONTHLY}>Repetir Mensalmente</option>
                <option value={Recurrence.YEARLY}>Repetir Anualmente</option>
              </select>
            </div>
          </div>

          {/* Group Toggle (Only for Expenses) */}
          {type === 'expense' && (
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Conta</label>
              <div className="bg-brand-gray dark:bg-zinc-800 rounded-2xl p-2 flex">
                 <button 
                   type="button"
                   onClick={() => setGroupId('none')}
                   className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${groupId === 'none' ? 'bg-white dark:bg-zinc-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                 >
                   Pessoal
                 </button>
                 <button 
                   type="button"
                   onClick={() => setGroupId(groups?.[0]?.id || 'g1')}
                   className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${groupId !== 'none' ? 'bg-white dark:bg-zinc-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                 >
                   Grupo
                 </button>
              </div>
            </div>
          )}
          
          {groupId !== 'none' && type === 'expense' && (
             <div className="animate-in fade-in slide-in-from-top-2">
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Selecione o Grupo</label>
                <select 
                  className="w-full px-4 py-3 bg-white dark:bg-zinc-700 border-2 border-brand-gray dark:border-zinc-800 rounded-xl mb-3 text-slate-800 dark:text-white font-medium focus:outline-none"
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                >
                  <option value="" disabled>Selecione o grupo</option>
                  {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                
                {/* Visual Group Member Selector */}
                <div className="flex -space-x-2 overflow-hidden py-2 pl-1 mb-2">
                  {groups?.find(g => g.id === groupId)?.members?.map((m, i) => (
                    <div key={m.id} className="w-8 h-8 rounded-full border-2 border-white dark:border-zinc-800 bg-slate-200 dark:bg-zinc-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300" title={m.name}>
                       {m.avatarUrl ? <img src={m.avatarUrl} className="w-full h-full rounded-full object-cover" /> : m.name[0]}
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-3 bg-brand-lime/10 p-3 rounded-xl border border-brand-lime/20">
                   <input 
                    type="checkbox" 
                    id="split"
                    checked={splitEqually}
                    onChange={(e) => setSplitEqually(e.target.checked)}
                    className="w-5 h-5 text-brand-dark rounded focus:ring-brand-lime border-gray-300"
                   />
                   <label htmlFor="split" className="text-sm font-bold text-slate-700 dark:text-slate-200">Dividir igualmente?</label>
                </div>
             </div>
          )}

          <button 
            type="submit" 
            className="w-full bg-brand-dark dark:bg-white text-brand-lime dark:text-black py-5 rounded-2xl font-bold text-lg hover:shadow-lg hover:shadow-brand-dark/30 transition-all active:scale-[0.98] mt-4 flex justify-center items-center gap-2"
          >
            {expenseToEdit ? 'Salvar Alterações' : `Confirmar ${type === 'expense' ? 'Despesa' : 'Entrada'}`}
          </button>
        </form>
      </div>
    </div>
  );
};